<template>
  <container v-if="chartData">
    <ParentSize @resize="setSize">
      <TooltipProvider
        :eventBus="tooltipEventBus"
        :chart-metadata="{ series, stroke }"
        :tooltipContentComponent="nodeTooltipContentComponent"
        :tooltipContainerComponent="nodeTooltipContainerComponent"
      >
        <ChartSvg :width="parentWidth" :height="parentHeight">
          <AreaChartHover
            :y-scale="yScale"
            :labels-scale="labelsScale"
            :data="chartData"
            :x-prop="xProp"
            :serie="series[0]"
            :stroke="stroke({ serieIndex: 0 })"
            :chartWidth="chartWidth"
            @enter="showTooltip($event.id, $event)"
            @leave="hideTooltip($event.id)"
          >
            <rect :width="parentWidth" :height="parentHeight" fill="transparent"></rect>
            <FilteredData>
              <AreaSerie
                v-for="(serie, index) in series"
                :key="index"
                :data="filteredData"
                :serie="serie"
                :serie-index="index"
                :series="series"
                :y-scale="yScale"
                :labels-scale="labelsScale"
                :x-prop="xProp"
                :fill="fill"
                :stroke="stroke"
                :strokeWidth="strokeWidth"
                :strokeVisible="strokeVisible"
                :strokeDashedArray="strokeDashedArray"
                :noDataStrokeVisible="noDataStrokeVisible"
                :noDataStrokeWidth="noDataStrokeWidth"
                :noDataStrokeDashedArray="noDataStrokeDashedArray"
              />
            </FilteredData>

            <component
              v-bind="axesAttrs"
              :is="axesComponent"
              :xScale="labelsScale"
              :yScale="yScale"
              :x="leftSpace"
              :y="topSpace"
              :setSpace="setSpace"
              :width="chartWidth"
              :height="chartHeight"
            />
            <g class="series">
              <AreaSerie
                v-for="(serie, serieIndex) in series"
                :key="serieIndex"
                :data="chartData"
                :serie="serie"
                :serie-index="serieIndex"
                :series="series"
                :y-scale="yScale"
                :labels-scale="labelsScale"
                :x-prop="xProp"
                :fill="fill"
                :stroke="stroke"
                :strokeWidth="strokeWidth"
                :strokeVisible="strokeVisible"
                :strokeDashedArray="strokeDashedArray"
                :noDataStrokeVisible="noDataStrokeVisible"
                :noDataStrokeWidth="noDataStrokeWidth"
                :noDataStrokeDashedArray="noDataStrokeDashedArray"
              />
            </g>
            <TotalCopy
              v-for="(serie, serieIndex) in series"
              :lastItem="chartData[chartData.length - 1]"
              :y-scale="yScale"
              :parentWidth="parentWidth"
              :serie="serie"
              :key="serieIndex"
              :color="stroke({ serieIndex })"
              :isTotal="isTotal"
            />
          </AreaChartHover>
        </ChartSvg>
      </TooltipProvider>
    </ParentSize>
  </container>
</template>

<script>
import { scaleLinear, scalePoint } from 'd3-scale';
import { prop, flatten, pipe, map } from 'ramda';

import { ChartSvg } from '../base';
import { Container } from '../charts.styles';
import { FilteredData } from './areaChart.styles';
import { ParentSize, ParentSizeMixin } from '../../responsive';
import { ChartsMixin } from '../charts.mixin';
import { fill, stroke } from './areaChart.config';
import AreaSerie from './areaSerie.component';
import { ChartAxesMixin } from '../axes/axes.mixins';
import Axes from '../axes/axes.component';
import { TooltipProviderMixin } from '../tooltip';
import AreaChartHover from './areaChartHover.component';
import TotalCopy from './totalCopy.component';

import SerieTooltipContent from './serieTooltip/serieTooltipContent.component';
import { getSeriesDomain } from '../charts.helper';
import { extent } from 'd3-array';

export default {
  mixins: [ChartsMixin, ChartAxesMixin, ParentSizeMixin, TooltipProviderMixin],
  components: { ChartSvg, Container, ParentSize, AreaSerie, Axes, AreaChartHover, TotalCopy, FilteredData },
  props: {
    data: Array,
    filteredData: { type: Array, default: () => [] },
    axesComponent: { default: () => Axes },
    series: { type: Array, default: () => [] },
    xProp: String,
    yDomain: { type: Array, default: () => [] },
    isTotal: { type: Boolean, default: false },
    fill: { type: Function, default: fill },
    stroke: { type: Function, default: stroke },
    strokeWidth: { type: Number, default: 1 },
    strokeVisible: { type: Boolean, default: false },
    strokeDashedArray: { type: String, required: false },
    noDataStrokeVisible: { type: Boolean, default: true },
    noDataStrokeWidth: { type: Number, default: 1 },
    noDataStrokeDashedArray: { type: String, default: '2 3' },
    nodeTooltipContainerComponent: { type: Object },
    nodeTooltipContentComponent: { type: Object, default: () => SerieTooltipContent },
  },
  computed: {
    labelsScale() {
      const labels = this.chartData.map(prop(this.xProp));
      const labelsScale = scalePoint().domain(labels);

      labelsScale.padding(labels.length * 0.03).align(0);

      labelsScale.range([this.leftSpace, this.leftSpace + this.chartWidth]);

      return labelsScale;
    },
    yScale() {
      const [minValue, maxValue] = pipe(
        map(item => this.series.map(el => item[el])),
        flatten,
        extent
      )(this.chartData);
      const min = minValue - (maxValue - minValue) * 0.1;

      return scaleLinear()
        .domain(
          getSeriesDomain({
            forceDomain: [minValue >= 0 && min < 0 ? 0 : min, maxValue],
            series: this.series,
            data: this.chartData,
          })
        )
        .range([this.topSpace + this.chartHeight, this.topSpace]);
    },
  },
};
</script>
